import { useState, useEffect, useCallback, useRef } from 'react'
import { createAICommand, AICommandType, AICommandStatus, isCommandComplete } from '@/utils/aiCommandUtils'

interface Section {
  id: string
  title: string
  content: string
}

interface DocumentNodeData {
  selectedSection?: Section | null
  sectionStates?: Record<string, string>
  sectionCommand?: any
  sectionReviews?: Record<string, any>
  contentMarkdown?: string
  processingStates?: Record<string, boolean>
  lastModified?: number
  sectionExplanations?: Record<string, string>
  sectionExplanationStates?: Record<string, boolean>
}

const useSectionManagement = (
  id: string,
  data: DocumentNodeData,
  syncNodeData: (newData: Partial<DocumentNodeData>) => void,
  contentMarkdown: string | undefined,
  setContentMarkdown: (markdown: string) => void
) => {
  const [localSections, setLocalSections] = useState<Map<string, Section>>(new Map())
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
  const [editingSectionContent, setEditingSectionContent] = useState<string>('')
  const [isEditingSection, setIsEditingSection] = useState(false)
  const [sectionStates, setSectionStates] = useState<Record<string, string>>(data.sectionStates || {})
  const [sectionReviews, setSectionReviews] = useState<Record<string, any>>(data.sectionReviews || {})

  // Helper function to clean up orphaned explanations when sections change
  const syncExplanationsWithSections = useCallback((currentSections: Map<string, Section>) => {
    const explanations = data.sectionExplanations || {}
    const explanationStates = data.sectionExplanationStates || {}

    // Get current section IDs
    const currentSectionIds = new Set(currentSections.keys())

    // Remove explanations for sections that no longer exist
    const cleanedExplanations = Object.fromEntries(
      Object.entries(explanations).filter(([sectionId]) => currentSectionIds.has(sectionId))
    )
    const cleanedExplanationStates = Object.fromEntries(
      Object.entries(explanationStates).filter(([sectionId]) => currentSectionIds.has(sectionId))
    )

    // Only sync if there are changes
    const hasExplanationChanges = Object.keys(explanations).length !== Object.keys(cleanedExplanations).length ||
      Object.keys(explanationStates).length !== Object.keys(cleanedExplanationStates).length

    if (hasExplanationChanges) {
      syncNodeData({
        sectionExplanations: cleanedExplanations,
        sectionExplanationStates: cleanedExplanationStates
      })
    }
  }, [data.sectionExplanations, data.sectionExplanationStates, syncNodeData])

  useEffect(() => {
    if (!contentMarkdown) return
    
    const newSections = new Map<string, Section>()
    const lines = contentMarkdown.split('\n')
    let currentSectionTitle = ''
    let currentSectionContent: string[] = []
    let sectionCount = 0
    let inSection = false
    const sectionRegex = /^:::\s*section\s+(.*)$/

    lines.forEach((line, lineIndex) => {
      const sectionStart = sectionRegex.exec(line)
      const sectionEnd = line.trim() === ':::'
      if (sectionStart) {
        inSection = true
        currentSectionTitle = sectionStart[1]?.trim() || 'Unnamed Section'
        // Section parsing for debugging when needed
      } else if (sectionEnd && inSection) {
        inSection = false
        if (currentSectionTitle) {
          sectionCount++
          const sectionId = `section-${sectionCount}`
          const content = currentSectionContent.join('\n').trim()
          newSections.set(sectionId, { id: sectionId, title: currentSectionTitle, content })
        }
        currentSectionTitle = ''
        currentSectionContent = []
      } else if (inSection) {
        currentSectionContent.push(line)
      }
    })

    // 🚨 CRITICAL: Actually update the local sections map!
    setLocalSections(newSections)

    // Sync explanations with current sections
    syncExplanationsWithSections(newSections)
  }, [contentMarkdown, syncExplanationsWithSections])

  // --- Sync local state from collaborative state (one field at a time) ---
  useEffect(() => {
    setSectionStates(data.sectionStates || {})
  }, [data.sectionStates])

  useEffect(() => {
    setSectionReviews(data.sectionReviews || {})
  }, [data.sectionReviews])

  useEffect(() => {
    if (data.selectedSection) setSelectedSectionId(data.selectedSection.id)
  }, [data.selectedSection])

  useEffect(() => {
    // Process section commands when they complete

    if (
      data.sectionCommand &&
      isCommandComplete(data.sectionCommand, AICommandType.SECTION_PROCESS) &&
      data.sectionCommand.for_review &&
      data.sectionCommand.sectionId
    ) {
      const sectionId = data.sectionCommand.sectionId
      const commandType = data.sectionCommand.command
      
      // Only clear sectionCommand if section does not exist
      if (!localSections.has(sectionId)) {
        syncNodeData({ sectionCommand: null })
      } else {
        // Always create review when AI processing is complete with for_review content
        const newReview = {
          originalContent: data.sectionCommand.content || '',
          suggestedContent: data.sectionCommand.for_review || '',
          commandType: data.sectionCommand.command || '',
        }

        // Clear the processing state when review is received
        const updatedProcessingStates = {
          ...(data.processingStates || {}),
          [sectionId]: false
        }

        syncNodeData({
          sectionCommand: null,
          sectionReviews: { ...data.sectionReviews, [sectionId]: newReview },
          processingStates: updatedProcessingStates
        })
      }
      // If review already processed and section exists, do nothing (let UI show the review)
    } else if (
      data.sectionCommand &&
      data.sectionCommand.processing === false &&
      data.sectionCommand.sectionId &&
      !data.sectionCommand.for_review
    ) {
      // Handle commands that completed but don't have for_review (direct content updates)
      const sectionId = data.sectionCommand.sectionId
      const command = data.sectionCommand.command
      
      // Only clear immediately for commands that don't create reviews
      // Commands like DivideIntoSections, ExpandDetail should wait for for_review
      const commandsThatDontCreateReviews = ['Draft', 'Detailed', 'Styled']
      const shouldClearImmediately = commandsThatDontCreateReviews.includes(command)
      
      if (shouldClearImmediately) {
        // Clear the processing state and command when processing is complete
        const updatedProcessingStates = {
          ...(data.processingStates || {}),
          [sectionId]: false
        }

        syncNodeData({
          sectionCommand: null,
          processingStates: updatedProcessingStates
        })
      } else {
        // For other commands, check if they've been waiting too long (timeout after 10 seconds)
        const commandTimestamp = data.sectionCommand.processedAt || data.sectionCommand.timestamp || Date.now()
        const hasTimedOut = Date.now() - commandTimestamp > 10000 // 10 seconds
        
        if (hasTimedOut) {
          console.warn(`Command ${command} for section ${sectionId} timed out waiting for review, clearing...`)
          const updatedProcessingStates = {
            ...(data.processingStates || {}),
            [sectionId]: false
          }

          syncNodeData({
            sectionCommand: null,
            processingStates: updatedProcessingStates
          })
        }
      }
    }
  }, [data.sectionCommand, localSections, syncNodeData, data.sectionReviews, data.processingStates])

  const handleSectionSelect = useCallback((sectionId: string, sectionName: string, event: React.MouseEvent) => {
    setSelectedSectionId(sectionId)
    const section = localSections.get(sectionId)
    if (section) {
      setIsEditingSection(false)

      const flowSectionSelect = (window as any).__flowSectionSelect
      if (flowSectionSelect) {
        flowSectionSelect(id, sectionId, sectionName || section.title, {
          clientX: event.clientX,
          clientY: event.clientY,
          pageX: event.pageX,
          pageY: event.pageY,
          screenX: event.screenX,
          screenY: event.screenY,
          type: event.type,
        })
      }

      syncNodeData({
        selectedSection: {
          id: section.id,
          title: section.title,
          content: section.content
        }
      })
    }
  }, [id, localSections, syncNodeData])

  const handleSectionEdit = (newContent: string) => setEditingSectionContent(newContent)

  const handleSectionEditSave = useCallback(() => {
    if (!selectedSectionId || !localSections.has(selectedSectionId) || !contentMarkdown) return
    const section = localSections.get(selectedSectionId)
    if (!section) return
    setIsEditingSection(false)
    const updatedSection = { ...section, content: editingSectionContent }
    const newSections = new Map(localSections)
    newSections.set(selectedSectionId, updatedSection)
    setLocalSections(newSections)
    const lines = contentMarkdown.split('\n')
    let inTargetSection = false
    const newLines: string[] = []
    let skipNext = false
    const sectionRegex = /^:::\s*section\s+(.*)$/
    lines.forEach((line) => {
      if (skipNext) { skipNext = false; return }
      const sectionStart = sectionRegex.exec(line)
      const sectionEnd = line.trim() === ':::'
      if (sectionStart) {
        const title = sectionStart[1]?.trim() || 'Unnamed Section'
        if (title === section.title) {
          inTargetSection = true
          newLines.push(line)
          newLines.push(...editingSectionContent.split('\n'))
          skipNext = true
        } else {
          newLines.push(line)
        }
      } else if (sectionEnd) {
        inTargetSection = false
        newLines.push(line)
      } else if (!inTargetSection) {
        newLines.push(line)
      }
    })
    const updatedMarkdown = newLines.join('\n')
    syncNodeData({ contentMarkdown: updatedMarkdown, lastModified: Date.now() })
  }, [selectedSectionId, localSections, editingSectionContent, contentMarkdown, setContentMarkdown, syncNodeData])

  const handleStartEditing = useCallback(() => setIsEditingSection(true), [])
  const handleCancelEditing = useCallback(() => {
    setIsEditingSection(false)
    if (selectedSectionId) {
      const section = localSections.get(selectedSectionId)
      if (section) setEditingSectionContent(section.content)
    }
  }, [selectedSectionId, localSections])

  const handleSectionStateChange = (state: 'Draft' | 'Detailed' | 'Styled') => {
    if (!selectedSectionId || !localSections.has(selectedSectionId)) return
    const section = localSections.get(selectedSectionId)
    if (!section) return
    
    // Allow re-running commands - no longer tracking processed commands
    
    const updatedSectionStates = { ...sectionStates, [selectedSectionId]: state }
    const sectionCommand = createAICommand(AICommandType.SECTION_PROCESS, AICommandStatus.STARTED, {
      command: state,
      sectionId: selectedSectionId,
      sectionTitle: section.title,
      content: section.content,
    })
    syncNodeData({ sectionCommand, sectionStates: updatedSectionStates })
  }

  const handleAcceptReview = (sectionId: string) => {
    const review = sectionReviews[sectionId]
    if (!review || !localSections.has(sectionId) || !contentMarkdown) return
    const section = localSections.get(sectionId)!
    // No longer tracking processed commands - allow multiple reviews
    const updatedReviews = { ...sectionReviews }
    delete updatedReviews[sectionId]
    const updatedSectionStates = { ...sectionStates, [sectionId]: review.commandType }

    // Process review for section

    // Parse multiple sections from suggestedContent
    const suggestedContent = review.suggestedContent
    const parsedSections: { title: string; content: string }[] = []
    const lines = suggestedContent.split('\n')
    let currentSectionTitle = ''
    let currentSectionContent: string[] = []
    let inSection = false
    const parsingSectionRegex = /^:::\s*(.*)$/

    lines.forEach((line) => {
      const sectionMatch = parsingSectionRegex.exec(line)
      if (sectionMatch && sectionMatch[1].trim() !== '') {
        // Start of a new section
        if (currentSectionTitle && currentSectionContent.length > 0) {
          // Save the previous section
          parsedSections.push({
            title: currentSectionTitle,
            content: currentSectionContent.join('\n').trim()
          })
        }
        inSection = true
        currentSectionTitle = sectionMatch[1].trim()
        currentSectionContent = []
      } else if (line.trim() === ':::' && inSection) {
        // End of current section
        if (currentSectionTitle && currentSectionContent.length > 0) {
          parsedSections.push({
            title: currentSectionTitle,
            content: currentSectionContent.join('\n').trim()
          })
        }
        inSection = false
        currentSectionTitle = ''
        currentSectionContent = []
      } else if (inSection) {
        currentSectionContent.push(line)
      }
    })

    // Handle case where content doesn't end with :::
    if (currentSectionTitle && currentSectionContent.length > 0) {
      parsedSections.push({
        title: currentSectionTitle,
        content: currentSectionContent.join('\n').trim()
      })
    }

    // If no sections were parsed, treat as single content block
    if (parsedSections.length === 0) {
      const cleanContent = suggestedContent.replace(/^:::\s*section\s+.*$/gm, '').replace(/^:::$/gm, '').trim()
      parsedSections.push({
        title: section.title,
        content: cleanContent
      })
    }

    // Parsed sections from AI response

    // Replace the original section with the parsed sections
    const escapedSectionTitle = section.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape regex special characters
    
    // Create a more precise regex that matches the exact section format
    // Use word boundaries and exact spacing to avoid partial matches
    const replaceSectionRegex = new RegExp(`^:::\\s*section\\s+${escapedSectionTitle}\\s*$`, 'i')
    
    const markdownLines = contentMarkdown.split('\n')
    const newLines: string[] = []
    let insideTargetSection = false
    let sectionFound = false

    // Replace section in markdown

    for (let i = 0; i < markdownLines.length; i++) {
      const line = markdownLines[i]
      
      if (replaceSectionRegex.test(line) && !sectionFound) {
        sectionFound = true
        insideTargetSection = true

        // Insert all parsed sections to replace the original one
        parsedSections.forEach((parsedSection) => {
          newLines.push(`:::section ${parsedSection.title}`)
          newLines.push(...parsedSection.content.split('\n'))
          newLines.push(':::')
        })

        // Skip the entire original section: keep advancing until we find the closing :::
        while (i + 1 < markdownLines.length) {
          i++ // Move to next line
          const nextLine = markdownLines[i]
          if (nextLine.trim() === ':::') {
            // Found the closing :::, we're done skipping
            insideTargetSection = false
            break
          }
        }
      } else if (!insideTargetSection) {
        // Only add lines that are not inside the section we're replacing
        newLines.push(line)
      }
      // Note: we don't add lines that are inside the target section since we're replacing it
    }

    // Section replacement completed

    if (sectionFound) {
      const updatedMarkdown = newLines.join('\n')
      setContentMarkdown(updatedMarkdown)
      
      // Update sections map with new sections
      const newSections = new Map(localSections)
      newSections.delete(sectionId) // Remove original section

      // Add new sections with incremented IDs
      let maxSectionNum = 0
      localSections.forEach((_, id) => {
        const match = /section-(\d+)/.exec(id)
        if (match) {
          maxSectionNum = Math.max(maxSectionNum, parseInt(match[1]))
        }
      })

      // Handle explanation synchronization when sections change
      const updatedExplanations = { ...(data.sectionExplanations || {}) }
      const updatedExplanationStates = { ...(data.sectionExplanationStates || {}) }

      // If multiple sections are created, delete all explanations since section order changes
      if (parsedSections.length > 1) {
        // Clear all explanations when multiple sections are created (section order changes)
        Object.keys(updatedExplanations).forEach(key => delete updatedExplanations[key])
        Object.keys(updatedExplanationStates).forEach(key => delete updatedExplanationStates[key])
      } else {
        // Single section replacement - preserve explanation from original section if it exists
        const originalExplanation = updatedExplanations[sectionId]
        const originalExplanationState = updatedExplanationStates[sectionId]

        // Remove explanation for original section
        delete updatedExplanations[sectionId]
        delete updatedExplanationStates[sectionId]

        // For single section replacement, preserve the explanation
        if (parsedSections.length === 1 && originalExplanation) {
          const newSectionId = `section-${maxSectionNum + 1}`
          updatedExplanations[newSectionId] = originalExplanation
          updatedExplanationStates[newSectionId] = originalExplanationState || false
        }
      }

      parsedSections.forEach((parsedSection, index) => {
        const newSectionId = `section-${maxSectionNum + index + 1}`
        newSections.set(newSectionId, {
          id: newSectionId,
          title: parsedSection.title,
          content: parsedSection.content
        })
      })

      setLocalSections(newSections)

      // Clear the processing state properly
      const updatedProcessingStates = { ...(data.processingStates || {}) }
      delete updatedProcessingStates[sectionId] // Remove the old processing state

      // Sync final state

      // Only sync upstream here with all changes
      syncNodeData({
        contentMarkdown: updatedMarkdown,
        sectionStates: updatedSectionStates,
        sectionReviews: updatedReviews,
        sectionExplanations: updatedExplanations,
        sectionExplanationStates: updatedExplanationStates,
        sectionCommand: null,
        processingStates: updatedProcessingStates, // Clear processing states
        lastModified: Date.now(),
      })
    } else {
      // Section not found in markdown, removing review only
      syncNodeData({
        sectionStates: updatedSectionStates,
        sectionReviews: updatedReviews,
        sectionCommand: null,
        lastModified: Date.now(),
      })
    }
  }

  const handleRejectReview = (sectionId: string) => {
    const updatedReviews = { ...sectionReviews }
    delete updatedReviews[sectionId]
    // Only sync upstream here
    syncNodeData({ sectionReviews: updatedReviews, sectionCommand: null, lastModified: Date.now() })
  }

  return {
    sections: localSections,
    selectedSectionId,
    setSelectedSectionId,
    editingSectionContent,
    setEditingSectionContent,
    isEditingSection,
    setIsEditingSection,
    sectionStates,
    setSectionStates,
    sectionReviews,
    setSectionReviews,
    handleSectionSelect,
    handleSectionEdit,
    handleSectionEditSave,
    handleStartEditing,
    handleCancelEditing,
    handleSectionStateChange,
    handleAcceptReview,
    handleRejectReview,
  }
}

export default useSectionManagement